import React, { useState, useEffect, useCallback } from 'react';
import { takeScreenshot } from '@/services/screenshotService';
import { Loader2 } from 'lucide-react';

interface WebScreenshotProps {
  url: string;
  title?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function WebScreenshot({ url, title, size = 'md', className = '' }: WebScreenshotProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);

  const sizeClasses = {
    sm: 'w-32 h-24',
    md: 'w-48 h-36',
    lg: 'w-64 h-48'
  };

  const cleanupUrl = useCallback((url: string) => {
    if (url) {
      URL.revokeObjectURL(url);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const getScreenshot = async () => {
      if (!url) {
        setError('URL is required');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const result = await takeScreenshot(url);
        
        if (isMounted) {
          // Clean up previous URL if it exists
          if (screenshotUrl) {
            cleanupUrl(screenshotUrl);
          }
          setScreenshotUrl(result);
        }
      } catch (err) {
        if (isMounted) {
          console.error('Screenshot error:', err);
          setError(err instanceof Error ? err.message : 'Failed to load screenshot');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    getScreenshot();

    // Cleanup function
    return () => {
      isMounted = false;
      if (screenshotUrl) {
        cleanupUrl(screenshotUrl);
      }
    };
  }, [url, cleanupUrl, screenshotUrl]);

  return (
    <div className={`relative rounded-lg overflow-hidden ${sizeClasses[size]} ${className}`}>
      {isLoading && <Loader2 className="w-8 h-8 animate-spin text-blue-500" />}
      
      {error ? (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 text-gray-500 text-sm p-2 text-center">
          {error}
        </div>
      ) : screenshotUrl ? (
        <img
          src={screenshotUrl}
          alt={title || 'Website preview'}
          className={`w-full h-full object-cover ${isLoading ? 'opacity-0' : 'opacity-100 transition-opacity duration-200'}`}
          onError={() => {
            setError('Failed to load image');
          }}
        />
      ) : null}

      {title && (
        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 truncate">
          {title}
        </div>
      )}
    </div>
  );
}
